import { useCreateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState
} from 'react';
import { matchPath, useLocation } from 'react-router';
import { urlStructureDetailView } from '../../../routing/routingPaths';

export type AssetViewedContextType = {
  setViewed: (id: string) => void;
};
/**Determine if an asset is viewed and limit it to once per opened detail view */
export const assetViewedContext = createContext<AssetViewedContextType>(null);

interface Props extends PropsWithChildren {
  /** Active asset id */
  assetId: string;
}

const cacheInvalidator = (r, anc) => [
  { type: anc.type, id: anc.id, relationship: 'activities' }
];

export const AssetViewedProvider = (props: Props) => {
  const { assetId } = props;

  // Track all the viewed assets
  const [viewedAssets, setViewedAssets] = useState<string[]>([]);

  const { pathname } = useLocation();
  const inDetailView = matchPath(pathname, {
    path: urlStructureDetailView
  });

  const currentTab = inDetailView && inDetailView.params?.['tab'];

  const [createResource] = useCreateResource__ALPHA({
    ancestor: { type: 'asset', id: assetId },
    resourceType: 'activity',
    cacheInvalidator
  });

  const context = useMemo(() => {
    return {
      setViewed: (id: string) => {
        if (
          (inDetailView && currentTab !== 'overview') ||
          viewedAssets.includes(id)
        )
          return;
        // Update state
        setViewedAssets([...viewedAssets, id]);

        /** Manually create "viewed" activity if preview was seen */
        createResource({
          type: 'activity',
          action: 'AssetPreviewed'
        });
      }
    };
  }, [currentTab, assetId, viewedAssets, createResource, setViewedAssets]);

  return (
    <assetViewedContext.Provider value={context}>
      {props.children}
    </assetViewedContext.Provider>
  );
};

export const useAssetViewed = () => {
  return useContext(assetViewedContext);
};
